// pages/IssueDetail.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Divider,
  Grid,
  Typography,
  Button,
  Stack,
  Card,
  CardMedia,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import TableChartIcon from "@mui/icons-material/TableChart";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import CodeIcon from "@mui/icons-material/Code";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import ArchiveIcon from "@mui/icons-material/Archive";
import ShareIcon from "@mui/icons-material/Share";
import DownloadIcon from "@mui/icons-material/Download";
import {
  addDoc,
  getDoc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { auth } from "../firebase";
import { propertyCollection, propertyDoc } from "../utils/firestorePaths";
import { httpsCallable, getFunctions } from "firebase/functions";
import PropertyContext from "../contexts/PropertyContext";
import { completeMeta, updateMeta } from "../utils/auditFields";
import IssueThread from "../components/PropertyIssues/IssueThread";
import generateSingleIssuePDF from "./generateSingleIssuePDF";

const PRIORITY_CHIP = {
  low: { color: "success", label: "Low" },
  medium: { color: "warning", label: "Medium" },
  high: { color: "error", label: "High" },
  urgent: { color: "error", label: "Urgent" },
};

const STATUS_OPTIONS = [
  "not reported",
  "reported",
  "pending estimate",
  "pending approval",
  "pending vendor arrival",
  "work in progress",
  "completed",
];

const ASSIGNED_TO_OPTIONS = ["IT", "Operations", "Facilities", "Marketing"];

// File type detection and icon mapping
const getFileTypeIcon = (fileName) => {
  const extension = fileName.split('.').pop()?.toLowerCase();

  switch (extension) {
    case 'pdf':
      return PictureAsPdfIcon;
    case 'doc':
    case 'docx':
      return DescriptionIcon;
    case 'xls':
    case 'xlsx':
    case 'csv':
      return TableChartIcon;
    case 'ppt':
    case 'pptx':
      return SlideshowIcon;
    case 'txt':
    case 'md':
      return DescriptionIcon;
    case 'js':
    case 'jsx':
    case 'ts':
    case 'tsx':
    case 'html':
    case 'css':
    case 'json':
    case 'xml':
      return CodeIcon;
    case 'mp3':
    case 'wav':
    case 'ogg':
    case 'flac':
      return AudiotrackIcon;
    case 'mp4':
    case 'avi':
    case 'mov':
    case 'wmv':
    case 'flv':
      return VideoLibraryIcon;
    case 'zip':
    case 'rar':
    case '7z':
    case 'tar':
    case 'gz':
      return ArchiveIcon;
    default:
      return InsertDriveFileIcon;
  }
};

const isImageFile = (fileName) => {
  const extension = fileName.split('.').pop()?.toLowerCase();
  return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension);
};

// File Carousel Component
const FileCarousel = ({ files }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!files || files.length === 0) return null;

  // Separate quotes from other files
  const quotes = files.filter(f => f.isQuote);
  const otherFiles = files.filter(f => !f.isQuote);

  const nextFile = () => {
    setCurrentIndex((prev) => (prev + 1) % files.length);
  };

  const prevFile = () => {
    setCurrentIndex((prev) => (prev - 1 + files.length) % files.length);
  };

  const openFile = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box sx={{ position: 'relative', maxWidth: '100%' }}>
      {/* Quotes Section */}
      {quotes.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Quotes ({quotes.length})
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            {quotes.map((file, index) => (
              <Tooltip key={`quote-${index}`} title={file.name || `Quote ${index + 1}`} arrow>
                <Card
                  sx={{
                    width: 120,
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: 3,
                    },
                  }}
                  onClick={() => openFile(file.url)}
                >
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'primary.light',
                      p: 2,
                    }}
                  >
                    <PictureAsPdfIcon sx={{ fontSize: 48, color: 'primary.dark', mb: 1 }} />
                    <Typography
                      variant="body2"
                      sx={{
                        textAlign: 'center',
                        fontWeight: 'bold',
                        color: 'primary.dark',
                      }}
                    >
                      Quote {index + 1}
                    </Typography>
                  </Box>
                </Card>
              </Tooltip>
            ))}
          </Box>
        </Box>
      )}

      {/* Other Files Section */}
      {otherFiles.length > 0 && (
        <Box>
          {quotes.length > 0 && (
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              Other Files ({otherFiles.length})
            </Typography>
          )}
          <Box
            sx={{
              display: 'flex',
              overflowX: 'auto',
              gap: 2,
              pb: 1,
              '&::-webkit-scrollbar': {
                height: 8,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(0,0,0,0.1)',
                borderRadius: 4,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.3)',
                borderRadius: 4,
              },
            }}
          >
            {otherFiles.map((file, index) => {
              const fileName = file.name || `file-${index + 1}`;
              const isImage = isImageFile(fileName);
              const FileIcon = getFileTypeIcon(fileName);

              return (
                <Tooltip key={`file-${index}`} title={fileName} arrow>
                  <Card
                    sx={{
                      minWidth: 120,
                      height: 120,
                      cursor: 'pointer',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: 3,
                      },
                    }}
                    onClick={() => openFile(file.url)}
                  >
                    {isImage ? (
                      <CardMedia
                        component="img"
                        height="120"
                        image={file.url}
                        alt={fileName}
                        sx={{
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'grey.100',
                          p: 2,
                        }}
                      >
                        <FileIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                        <Typography
                          variant="caption"
                          sx={{
                            textAlign: 'center',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: 1.2,
                          }}
                        >
                          {fileName.length > 15 ? `${fileName.substring(0, 12)}...` : fileName}
                        </Typography>
                      </Box>
                    )}
                  </Card>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default function IssueDetail({ userInfo }) {
  const { issueId } = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedProperty, selectedClient } = useContext(PropertyContext);
  const [issue, setIssue] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareEmail, setShareEmail] = useState("");
  const [shareLoading, setShareLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  // For facilities users, get property from URL parameter, otherwise use context
  const propertyId = searchParams.get('property') || selectedProperty;
  const clientId = searchParams.get('client') || selectedClient;

  const issueRef = useMemo(
    () =>
      propertyDoc(clientId, propertyId, "propertyIssues", issueId),
    [clientId, propertyId, issueId]
  );

  const vendorsRef = useMemo(
    () => propertyCollection(clientId, propertyId, "Vendors"),
    [clientId, propertyId]
  );

  useEffect(() => {
    if (!propertyId || !issueId) {
      console.error("Missing propertyId or issueId:", { propertyId, issueId });
      return;
    }

    const unsub = onSnapshot(issueRef, (snap) => {
      if (snap.exists()) {
        setIssue({ id: snap.id, ...snap.data() });
      } else {
        console.error("Issue document does not exist:", { propertyId, issueId });
        setIssue(null);
      }
    });
    return () => unsub();
  }, [issueRef, propertyId, issueId]);

  // Load vendors
  useEffect(() => {
    if (!propertyId) return;

    const unsubVendors = onSnapshot(query(vendorsRef), (snap) => {
      const vendorsList = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setVendors(vendorsList);
    });

    return () => unsubVendors();
  }, [vendorsRef, propertyId]);

  const markComplete = async () => {
    await updateDoc(issueRef, { completed: true, ...completeMeta(userInfo) });
  };

  const handleStatusChange = async (newStatus) => {
    await updateDoc(issueRef, { status: newStatus, ...updateMeta(userInfo) });
  };

  const handleAssignedToChange = async (newAssignedTo) => {
    await updateDoc(issueRef, { assignedTo: newAssignedTo });
  };

  const handleVendorChange = async (newVendor) => {
    await updateDoc(issueRef, { vendor: newVendor });
  };

  const handleGeneratePDF = async () => {
    if (!issue) return;
    setPdfLoading(true);
    try {
      await generateSingleIssuePDF(issue, propertyId);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const handleShareIssue = async () => {
    if (!shareEmail.trim() || !issue) return;
    setShareLoading(true);
    try {
      const functions = getFunctions();
      const shareIssueEmail = httpsCallable(functions, "shareIssueEmail");
      await shareIssueEmail({
        email: shareEmail.trim(),
        issueId: issue.id,
        propertyId: propertyId,
        clientId: clientId,
        issueDetails: issue.issueDetails,
        priority: issue.priority,
        status: issue.status,
        sharedBy: userInfo?.fullName || "Unknown",
      });

      // Add message to thread about the share
      const msgsRef = propertyCollection(
        clientId,
        propertyId,
        "propertyIssues",
        issueId,
        "messages"
      );
      const uid = auth.currentUser?.uid || "anon";
      const displayName = userInfo?.fullName || "Unknown";
      await addDoc(msgsRef, {
        text: `${displayName} shared this issue to ${shareEmail.trim()}`,
        createdAt: serverTimestamp(),
        createdBy: { uid, displayName },
        readBy: [uid],
        isSystemMessage: true,
      });

      setShareDialogOpen(false);
      setShareEmail("");
    } catch (error) {
      console.error("Error sharing issue:", error);
      alert("Error sharing issue. Please try again.");
    }
    setShareLoading(false);
  };

  if (!propertyId || !issueId) {
    return (
      <Box p={2}>
        <Typography color="error">
          Invalid issue URL. Missing property or issue ID.
        </Typography>
        <Button onClick={() => nav(-1)} sx={{ mt: 1 }}>
          ← Go Back
        </Button>
      </Box>
    );
  }

  if (!issue) {
    return (
      <Box p={2}>
        <Typography>Loading issue…</Typography>
        <Typography variant="caption" color="textSecondary">
          Property: {propertyId}, Issue: {issueId}
        </Typography>
      </Box>
    );
  }

  const p = PRIORITY_CHIP[issue.priority] || PRIORITY_CHIP.medium;

  return (
    <Box p={2} display="grid" gap={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={1}>
          <Button size="small" onClick={() => nav(-1)}>
            ← Back
          </Button>
          <Typography variant="h6">Issue #{issue.id.slice(0, 6)}</Typography>
          <Chip size="small" color={p.color} label={p.label} />
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title="Share Issue">
            <IconButton onClick={() => setShareDialogOpen(true)} color="primary">
              <ShareIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download PDF">
            <IconButton onClick={handleGeneratePDF} color="primary" disabled={pdfLoading}>
              {pdfLoading ? <CircularProgress size={24} /> : <PictureAsPdfIcon />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Divider />

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Typography variant="subtitle2" gutterBottom>
            Details
          </Typography>
          <Box
            sx={{ p: 2, border: "1px solid rgba(0,0,0,0.12)", borderRadius: 1 }}
          >
            {/* Status Dropdown */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: "bold" }}>
                Status:
              </Typography>
              <FormControl size="small" fullWidth>
                <Select
                  value={issue.status || "not reported"}
                  onChange={(e) => handleStatusChange(e.target.value)}
                >
                  {STATUS_OPTIONS.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Assigned To - Show dropdown only if not assigned yet */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: "bold" }}>
                Assigned To:
              </Typography>
              {issue.assignedTo ? (
                <Typography>{issue.assignedTo}</Typography>
              ) : (
                <FormControl size="small" fullWidth>
                  <Select
                    value=""
                    onChange={(e) => handleAssignedToChange(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      <em>Assign to...</em>
                    </MenuItem>
                    {ASSIGNED_TO_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>

            {/* Vendor - Searchable Autocomplete */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: "bold" }}>
                Vendor:
              </Typography>
              <Autocomplete
                size="small"
                options={[...vendors.map((v) => v.name), "unknown", "TBD"]}
                value={issue.vendor || "unknown"}
                onChange={(event, newValue) => {
                  if (newValue) handleVendorChange(newValue);
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select vendor" />
                )}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: "bold" }}>
              Description:
            </Typography>
            <Typography sx={{ whiteSpace: "pre-wrap" }}>
              {issue.issueDetails || "—"}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={markComplete}
              disabled={!!issue.completed}
            >
              {issue.completed ? "Completed" : "Mark Complete"}
            </Button>
          </Stack>
        </Grid>

        <Grid item xs={12} md={5}>
          <IssueThread issueId={issueId} clientId={clientId} propertyId={propertyId} height={420} userInfo={userInfo} />
        </Grid>

        {Array.isArray(issue.files) && issue.files.length > 0 && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Files ({issue.files.length})
            </Typography>
            <FileCarousel files={issue.files} />
          </Grid>
        )}
      </Grid>

      {/* Share Issue Dialog */}
      <Dialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Share Issue</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the email address to share this issue with. They will receive an email with the issue details.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Email Address"
            type="email"
            value={shareEmail}
            onChange={(e) => setShareEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !shareLoading) handleShareIssue();
            }}
            disabled={shareLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareDialogOpen(false)} disabled={shareLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleShareIssue}
            variant="contained"
            disabled={!shareEmail.trim() || shareLoading}
            startIcon={shareLoading ? <CircularProgress size={16} /> : <ShareIcon />}
          >
            {shareLoading ? "Sharing..." : "Share"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
